/**
 * Encapsulates all state and derived data for the MatterDetail page.
 * Keeps MatterDetail.tsx focused on layout and composition.
 */
import * as Sentry from '@sentry/react';
import { format } from 'date-fns';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { buildGetExpertByIdFromMatters } from '~/modules/matters/expertResolverFromMatters';
import { useMyMatterExpertsQuery } from '~/modules/matters/useMyMatterExpertsQuery';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useCurrentUser } from '~/shared/hooks/useCurrentUser';
import { toHtmlDate } from '~/shared/utils/toHtmlDate';
import { useMessages } from '~/hooks/useMessages';
import { useMattersQuery } from '~/modules/matters/useMattersQuery';
import { useMatterMutations } from '~/modules/matters/useMatterMutations';
import { useRetainerMutations } from '~/modules/matters/useRetainerMutations';
import { useToast } from '~/shared/contexts/ToastContext';
import { MOCK_ATTORNEYS, MOCK_EXPERTS } from '~/shared/fixtures/authUsers';
import {
  MatterStatus,
  MatterExpertStatus,
  type UpdateMatterInput,
} from '~/gql/graphql';
import type {
  MatterViewModel,
  MyMatterExpertsRow,
} from '~/modules/matters/matters.types';
import type { ApolloError } from '@apollo/client';
import { type Message, UserRole, type User } from '~/shared/types/connect';
import { ExpertStatus } from '~/modules/matters/detail/expertsTabUtils';
import {
  getExpertsOnMatter,
  getRetainerStatusDisplay,
} from '~/modules/matters/matterCardUtils';
import {
  expertHasActiveParticipationInMatterExperts,
  isHiredExpertOnMatter,
  isInvitedExpertOnMatter,
} from '~/modules/matters/matterExpertUtils';
import {
  attorneyUserFromMatterGraph,
  expertUserFromMatterExpertRow,
} from '~/modules/matters/matterParticipantDisplay';
import { ExpertRetainerRollupStatus } from '~/modules/matters/MatterCard.types';
import { getMatterDisplayHeading } from '~/shared/utils/matterDisplay';
import { MatterDetailStatus } from '~/modules/matters/matterDetailStatus';
import { useMatterDetailRetainers } from '~/modules/matters/useMatterDetailRetainers';
import type { RetainerFieldsFragment } from '~/gql/graphql';
function getUserById(id: string): User {
  const user = [...MOCK_ATTORNEYS, ...MOCK_EXPERTS].find((u) => u.id === id);
  return (
    user ?? {
      id: '',
      name: 'Unknown',
      role: UserRole.ATTORNEY,
      email: '',
      avatarUrl: undefined,
    }
  );
}

export interface ExpertsOnMatterResult {
  hiredExpertIds: string[];
  hiredExperts: User[];
  addedExperts: User[];
  hasAnyHiredExpert: boolean;
  expertRetainerStatuses: Array<{
    expertId: string;
    status: ExpertRetainerRollupStatus;
  }>;
}

export interface UseMatterDetailReturn {
  status: MatterDetailStatus;
  matterId: string | undefined;
  matter: MatterViewModel | null;
  /** Populated when the matters list query fails; may be set while status is still `ready` if Apollo has cached matter data. */
  mattersQueryError: Error | undefined;
  currentUser: User | null;
  navigate: ReturnType<typeof useNavigate>;
  getUserById: (id: string) => User;

  // Access & role
  isAttorney: boolean;
  isMatterClosed: boolean;
  canExpertRequestRetainer: boolean;
  isInvitedOnlyExpert: boolean;
  canAccessAsExpert: boolean;
  isExpertOnMatter: boolean;
  expertMustCompleteStripe: boolean;

  // Other party (attorney for expert, expert for attorney)
  otherParty: User | null;
  balance: {
    hoursPurchased: number;
    hoursUsed?: number;
    hoursRemaining?: number;
    flatAmountPurchased?: number;
  } | null;

  // Experts on matter
  expertsOnMatter: ExpertsOnMatterResult;

  // Retainers (Apollo `retainersForMatter`)
  activeRetainers: RetainerFieldsFragment[];
  flatRateRetainers: RetainerFieldsFragment[];
  pendingRequests: RetainerFieldsFragment[];
  retainerExpertsForFilter: Array<{ id: string; name: string }>;
  filteredPendingRequests: RetainerFieldsFragment[];
  filteredFlatRateRetainers: RetainerFieldsFragment[];
  filteredActiveRetainers: RetainerFieldsFragment[];
  historyRetainers: RetainerFieldsFragment[];
  isLoadingRetainers: boolean;
  retainersError: ApolloError | undefined;
  noRetainerBalance: boolean;
  hasHoursBalance: boolean;
  hasFlatBalance: boolean;
  hasAnyRetainerBalance: boolean;
  pendingApproveLabel: string;
  hasNoHiredExpert: boolean;
  showActivityAndBilling: boolean;

  // Experts tab
  showExpertsTab: boolean;
  hasAnyExperts: boolean;
  filteredHiredExperts: User[];
  expertStatusFilter: ExpertStatus;
  setExpertStatusFilter: (value: ExpertStatus) => void;

  // Right panel tab
  rightDetailTab: 'details' | 'retainers' | 'experts';
  setRightDetailTab: (value: 'details' | 'retainers' | 'experts') => void;
  retainerStatusFilter: 'all' | 'pending' | 'active' | 'completed';
  setRetainerStatusFilter: (
    value: 'all' | 'pending' | 'active' | 'completed',
  ) => void;
  retainerExpertFilter: string;
  setRetainerExpertFilter: (value: string) => void;

  // Modal state
  editMatterModalOpen: boolean;
  setEditMatterModalOpen: (open: boolean) => void;
  matterToEdit: MatterViewModel | null;
  setMatterToEdit: (matter: MatterViewModel | null) => void;
  chatSidebarOpen: boolean;
  setChatSidebarOpen: (open: boolean) => void;
  chatExpertId: string | null;
  setChatExpertId: (id: string | null) => void;
  retainerModalOpen: boolean;
  setRetainerModalOpen: (open: boolean) => void;
  retainerModalExpertId: string | null;
  setRetainerModalExpertId: (id: string | null) => void;
  agreementRequestId: string | null;
  setAgreementRequestId: (id: string | null) => void;
  viewAgreementRequestId: string | null;
  setViewAgreementRequestId: (id: string | null) => void;
  viewOverviewRequestId: string | null;
  setViewOverviewRequestId: (id: string | null) => void;
  declineRequestId: string | null;
  setDeclineRequestId: (id: string | null) => void;
  declineMatterModalOpen: boolean;
  setDeclineMatterModalOpen: (open: boolean) => void;
  addExpertsModalOpen: boolean;
  setAddExpertsModalOpen: (open: boolean) => void;
  removeExpertModal: { expertId: string; isInvited: boolean } | null;
  setRemoveExpertModal: (
    value: { expertId: string; isInvited: boolean } | null,
  ) => void;
  closeRetainerModal: { retainerId: string } | null;
  setCloseRetainerModal: (value: { retainerId: string } | null) => void;

  // Details panel
  nextDeadlineEdit: string;
  setNextDeadlineEdit: (value: string) => void;
  showDeadlinePicker: boolean;
  setShowDeadlinePicker: (value: boolean) => void;

  // Chat
  chatOtherParty: User | null;
  chatMessagesForSidebar: Message[];
  isChatLoading: boolean;
  chatError: ApolloError | null;
  openChatWithExpert: (expertId: string) => void;
  closeChatSidebar: () => void;
  sendMessage: (content: string) => Promise<void>;
  markConversationRead: () => Promise<void>;
  unreadCount: number;
  downloadHistory: () => Promise<void>;

  // Store actions
  getMatterById: (id: string) => MatterViewModel | undefined;
  getExpertById: (id: string) => User | undefined;
  closeMatter: (matterId: string) => Promise<void>;
  updateMatter: (
    matterId: string,
    patch: Partial<UpdateMatterInput>,
  ) => Promise<void>;
  /** Expert undo “work complete” — calls GraphQL `markRetainerWorkActive`. */
  markRetainerActive: (retainerRequestId: string) => Promise<void>;
  /** Retainer ID currently being marked active; null when no request is in flight. */
  activatingRetainerId: string | null;
  /** Expert marks scoped work finished — calls GraphQL `markRetainerWorkComplete`. */
  markRetainerWorkComplete: (retainerRequestId: string) => Promise<void>;
  /** Retainer ID currently being marked complete; null when no request is in flight. */
  completingRetainerId: string | null;
  /** Full list from `retainersForMatter` (exposed as `store.retainerRequests` for compatibility). */
  retainerRequests: RetainerFieldsFragment[];
  experts: MyMatterExpertsRow[];
  getRetainerStatusDisplay: typeof getRetainerStatusDisplay;
}

/** Grouped return shape for MatterDetail (matterContext, retainers, experts, modals, chat, actions, store). */
export interface MatterDetailGroupedReturn {
  matterContext: Pick<
    UseMatterDetailReturn,
    | 'status'
    | 'matterId'
    | 'matter'
    | 'mattersQueryError'
    | 'currentUser'
    | 'navigate'
    | 'isAttorney'
    | 'isMatterClosed'
    | 'canExpertRequestRetainer'
    | 'isInvitedOnlyExpert'
    | 'canAccessAsExpert'
    | 'isExpertOnMatter'
    | 'expertMustCompleteStripe'
    | 'otherParty'
    | 'getExpertById'
    | 'getUserById'
  >;
  retainers: Pick<
    UseMatterDetailReturn,
    | 'expertsOnMatter'
    | 'balance'
    | 'activeRetainers'
    | 'flatRateRetainers'
    | 'pendingRequests'
    | 'retainerExpertsForFilter'
    | 'filteredPendingRequests'
    | 'filteredFlatRateRetainers'
    | 'filteredActiveRetainers'
    | 'noRetainerBalance'
    | 'hasHoursBalance'
    | 'hasFlatBalance'
    | 'hasAnyRetainerBalance'
    | 'pendingApproveLabel'
    | 'hasNoHiredExpert'
    | 'showActivityAndBilling'
    | 'historyRetainers'
    | 'isLoadingRetainers'
    | 'retainersError'
    | 'retainerRequests'
    | 'activatingRetainerId'
    | 'completingRetainerId'
  >;
  experts: Pick<
    UseMatterDetailReturn,
    | 'showExpertsTab'
    | 'hasAnyExperts'
    | 'filteredHiredExperts'
    | 'showActivityAndBilling'
    | 'hasNoHiredExpert'
  >;
  modals: Pick<
    UseMatterDetailReturn,
    | 'editMatterModalOpen'
    | 'setEditMatterModalOpen'
    | 'matterToEdit'
    | 'setMatterToEdit'
    | 'rightDetailTab'
    | 'setRightDetailTab'
    | 'retainerStatusFilter'
    | 'setRetainerStatusFilter'
    | 'retainerExpertFilter'
    | 'setRetainerExpertFilter'
    | 'chatSidebarOpen'
    | 'setChatSidebarOpen'
    | 'chatExpertId'
    | 'setChatExpertId'
    | 'retainerModalOpen'
    | 'setRetainerModalOpen'
    | 'retainerModalExpertId'
    | 'setRetainerModalExpertId'
    | 'agreementRequestId'
    | 'setAgreementRequestId'
    | 'viewAgreementRequestId'
    | 'setViewAgreementRequestId'
    | 'viewOverviewRequestId'
    | 'setViewOverviewRequestId'
    | 'declineRequestId'
    | 'setDeclineRequestId'
    | 'declineMatterModalOpen'
    | 'setDeclineMatterModalOpen'
    | 'addExpertsModalOpen'
    | 'setAddExpertsModalOpen'
    | 'removeExpertModal'
    | 'setRemoveExpertModal'
    | 'closeRetainerModal'
    | 'setCloseRetainerModal'
    | 'nextDeadlineEdit'
    | 'setNextDeadlineEdit'
    | 'showDeadlinePicker'
    | 'setShowDeadlinePicker'
    | 'expertStatusFilter'
    | 'setExpertStatusFilter'
  >;
  chat: Pick<
    UseMatterDetailReturn,
    | 'chatOtherParty'
    | 'chatMessagesForSidebar'
    | 'isChatLoading'
    | 'chatError'
    | 'openChatWithExpert'
    | 'closeChatSidebar'
    | 'sendMessage'
    | 'markConversationRead'
    | 'unreadCount'
    | 'downloadHistory'
  >;
  actions: Pick<
    UseMatterDetailReturn,
    | 'getMatterById'
    | 'getExpertById'
    | 'closeMatter'
    | 'updateMatter'
    | 'markRetainerActive'
    | 'markRetainerWorkComplete'
  >;
  store: Pick<UseMatterDetailReturn, 'retainerRequests' | 'experts'>;
}

export function useMatterDetail(): MatterDetailGroupedReturn {
  const { matterId } = useParams<{ matterId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const locationState = location.state as {
    openExpertsTab?: boolean;
    openAgreementRequestId?: string;
  } | null;
  const openExpertsTab = locationState?.openExpertsTab === true;
  const openAgreementRequestIdFromState =
    locationState?.openAgreementRequestId ?? null;
  const { user: currentUser } = useCurrentUser();
  const {
    matters: allMatters,
    isLoading: isMattersQueryLoading,
    error: mattersQueryError,
  } = useMattersQuery();
  const {
    closeMatter: closeMatterMutation,
    updateMatter: updateMatterMutation,
  } = useMatterMutations();
  const { showToast } = useToast();
  const {
    markRetainerWorkActive: markRetainerWorkActiveMutation,
    activatingRetainerId,
    markRetainerWorkComplete: markRetainerWorkCompleteMutation,
    completingRetainerId,
  } = useRetainerMutations();

  const { experts: pickerExperts } = useMyMatterExpertsQuery();

  const getExpertById = useMemo(
    () => buildGetExpertByIdFromMatters(allMatters, pickerExperts),
    [allMatters, pickerExperts],
  );

  const [editMatterModalOpen, setEditMatterModalOpen] = useState(false);
  const [matterToEdit, setMatterToEdit] = useState<MatterViewModel | null>(
    null,
  );
  const [chatSidebarOpen, setChatSidebarOpen] = useState(false);
  const [chatExpertId, setChatExpertId] = useState<string | null>(null);
  const [retainerModalOpen, setRetainerModalOpen] = useState(false);
  const [retainerModalExpertId, setRetainerModalExpertId] = useState<
    string | null
  >(null);
  const [agreementRequestId, setAgreementRequestId] = useState<string | null>(
    null,
  );
  const [viewAgreementRequestId, setViewAgreementRequestId] = useState<
    string | null
  >(null);
  const [viewOverviewRequestId, setViewOverviewRequestId] = useState<
    string | null
  >(null);
  const [declineRequestId, setDeclineRequestId] = useState<string | null>(null);
  const [declineMatterModalOpen, setDeclineMatterModalOpen] = useState(false);
  const [addExpertsModalOpen, setAddExpertsModalOpen] = useState(false);
  const [removeExpertModal, setRemoveExpertModal] = useState<{
    expertId: string;
    isInvited: boolean;
  } | null>(null);
  const [closeRetainerModal, setCloseRetainerModal] = useState<{
    retainerId: string;
  } | null>(null);
  const [nextDeadlineEdit, setNextDeadlineEdit] = useState('');
  const [showDeadlinePicker, setShowDeadlinePicker] = useState(false);
  const [rightDetailTab, setRightDetailTab] = useState<
    'details' | 'retainers' | 'experts'
  >(openExpertsTab ? 'experts' : 'retainers');
  const [retainerStatusFilter, setRetainerStatusFilter] = useState<
    'all' | 'pending' | 'active' | 'completed'
  >('all');
  const [retainerExpertFilter, setRetainerExpertFilter] =
    useState<string>('all');
  const [expertStatusFilter, setExpertStatusFilter] = useState<ExpertStatus>(
    ExpertStatus.All,
  );

  const matter: MatterViewModel | null = matterId
    ? allMatters.find((matterItem) => matterItem.id === matterId) ?? null
    : null;

  useEffect(() => {
    setNextDeadlineEdit(toHtmlDate(matter?.nextDeadline));
  }, [matter?.id, matter?.nextDeadline]);

  useEffect(() => {
    if (openAgreementRequestIdFromState) {
      setAgreementRequestId(openAgreementRequestIdFromState);
    }
  }, [openAgreementRequestIdFromState]);

  const expertMustCompleteStripe =
    currentUser?.role === 'Expert' && !currentUser.stripeConnectOnboarded;

  useEffect(() => {
    if (expertMustCompleteStripe && matter) {
      navigate('/connect/setup-payouts', {
        replace: true,
      });
    }
  }, [expertMustCompleteStripe, matter, navigate]);

  const isAttorney = currentUser?.role === UserRole.ATTORNEY;

  const conversationExpertId = isAttorney
    ? chatExpertId
    : currentUser?.id ?? null;

  const {
    conversation,
    messages: chatMessages,
    isLoading: isChatLoading,
    error: chatError,
    sendMessage,
    markConversationRead,
    fetchAllMessages,
  } = useMessages({
    matterId: matterId ?? '',
    expertId: conversationExpertId ?? '',
    skip: !matterId || !conversationExpertId || !chatSidebarOpen,
  });

  const matterDetailRetainers = useMatterDetailRetainers({
    matterId,
    matter,
    retainerStatusFilter,
    retainerExpertFilter,
    getExpertById,
  });

  const {
    retainers: matterRetainersList,
    isLoadingRetainers,
    retainersError: retainersQueryError,
    balance: balanceFromRetainers,
    activeHourlyRetainers,
    activeFlatRetainers,
    pendingRequests,
    retainerExpertsForFilter,
    filteredPendingRequests,
    filteredActiveFlatRetainers,
    filteredActiveHourlyRetainers,
    noRetainerBalance,
    hasHoursBalance,
    hasFlatBalance,
    pendingApproveLabel,
    historyRetainers,
  } = matterDetailRetainers;

  const expertsOnMatter = useMemo((): ExpertsOnMatterResult => {
    if (!matter) {
      return {
        hiredExpertIds: [],
        hiredExperts: [],
        addedExperts: [],
        hasAnyHiredExpert: false,
        expertRetainerStatuses: [],
      };
    }
    return getExpertsOnMatter({
      matter,
      retainers: matterRetainersList,
      getExpertById,
    });
  }, [matter, matterRetainersList, getExpertById]);

  const balance = balanceFromRetainers;
  const activeRetainers = activeHourlyRetainers;
  const flatRateRetainers = activeFlatRetainers;
  const filteredActiveRetainers = filteredActiveHourlyRetainers;
  const filteredFlatRateRetainers = filteredActiveFlatRetainers;

  const otherParty = (() => {
    if (!matter) {
      return null;
    }
    if (currentUser?.role === 'Attorney') {
      const hiredRow = matter.matterExperts.find(
        (matterExpert) => matterExpert.status === MatterExpertStatus.Hired,
      );
      if (hiredRow) {
        return expertUserFromMatterExpertRow(hiredRow, getExpertById);
      }
      const invitedRow = matter.matterExperts.find(
        (matterExpert) => matterExpert.status === MatterExpertStatus.Invited,
      );
      if (invitedRow) {
        return expertUserFromMatterExpertRow(invitedRow, getExpertById);
      }
      return null;
    }
    return (
      attorneyUserFromMatterGraph(matter.attorney) ??
      getUserById(matter.attorneyId)
    );
  })();

  const isMatterClosed = matter?.status === MatterStatus.Closed;
  const canExpertRequestRetainer = Boolean(
    !isAttorney &&
      !isMatterClosed &&
      matter &&
      currentUser?.role === 'Expert' &&
      expertHasActiveParticipationInMatterExperts(
        matter.matterExperts,
        currentUser.id,
      ),
  );
  const isInvitedOnlyExpert =
    currentUser?.role === 'Expert' &&
    Boolean(
      matter &&
        isInvitedExpertOnMatter(matter.matterExperts, currentUser.id) &&
        !isHiredExpertOnMatter(matter.matterExperts, currentUser.id),
    );
  const canAccessAsExpert =
    currentUser?.role !== 'Expert' ||
    Boolean(
      matter &&
        expertHasActiveParticipationInMatterExperts(
          matter.matterExperts,
          currentUser?.id ?? '',
        ),
    ) ||
    expertsOnMatter.hiredExpertIds.includes(currentUser?.id ?? '');

  const chatOtherParty = (() => {
    if (!isAttorney || !matter) {
      return otherParty;
    }
    return chatExpertId
      ? getExpertById(chatExpertId) ?? null
      : expertsOnMatter.hiredExperts[0] ?? null;
  })();

  const openChatWithExpert = useCallback((expertId: string) => {
    setChatExpertId(expertId);
    setChatSidebarOpen(true);
  }, []);

  const closeChatSidebar = useCallback(() => {
    setChatSidebarOpen(false);
    setChatExpertId(null);
    markConversationRead().catch((error) => {
      Sentry.captureException(error, {
        tags: { feature: 'chat_mark_read_close' },
      });
    });
  }, [markConversationRead]);

  const markedReadConversationRef = useRef<string | null>(null);
  useEffect(() => {
    if (!chatSidebarOpen || !conversation?.id) {
      markedReadConversationRef.current = null;
      return;
    }
    if (markedReadConversationRef.current === conversation.id) {
      return;
    }
    markedReadConversationRef.current = conversation.id;

    const markRead = async () => {
      try {
        await markConversationRead();
      } catch (error) {
        Sentry.captureException(error, {
          tags: { feature: 'chat_mark_read_open' },
        });
      }
    };
    markRead();
  }, [chatSidebarOpen, conversation?.id, markConversationRead]);

  const openedChatFromUrlRef = useRef<string | null>(null);
  useEffect(() => {
    const chatParam = new URLSearchParams(location.search).get('chat');
    if (chatParam !== 'true' || !matter) {
      return;
    }
    if (openedChatFromUrlRef.current === matter.id) {
      return;
    }
    if (isAttorney) {
      const firstExpert =
        expertsOnMatter.hiredExperts[0] ?? expertsOnMatter.addedExperts[0];
      if (firstExpert) {
        openedChatFromUrlRef.current = matter.id;
        openChatWithExpert(firstExpert.id);
      }
    } else if (otherParty) {
      openedChatFromUrlRef.current = matter.id;
      setChatSidebarOpen(true);
    }
  }, [
    location.search,
    matter,
    isAttorney,
    expertsOnMatter.hiredExperts,
    expertsOnMatter.addedExperts,
    otherParty,
    openChatWithExpert,
    setChatSidebarOpen,
  ]);

  const openedTabFromUrlRef = useRef<string | null>(null);
  useEffect(() => {
    const tabParam = new URLSearchParams(location.search).get('tab');
    if (tabParam !== 'experts' || !matter) {
      return;
    }
    if (openedTabFromUrlRef.current === matter.id) {
      return;
    }
    openedTabFromUrlRef.current = matter.id;
    setRightDetailTab('experts');
  }, [location.search, matter, setRightDetailTab]);

  const chatParticipantsByEmail = useMemo(() => {
    const map: Record<string, { name: string; avatarUrl?: string }> = {};
    if (currentUser) {
      map[currentUser.email] = {
        name: currentUser.name,
        avatarUrl: currentUser.avatarUrl,
      };
    }
    if (chatOtherParty) {
      map[chatOtherParty.email] = {
        name: chatOtherParty.name,
        avatarUrl: chatOtherParty.avatarUrl,
      };
    }
    return map;
  }, [currentUser, chatOtherParty]);

  const chatMessagesForSidebar: Message[] = useMemo(() => {
    if (!matter || !chatMessages.length) {
      return [];
    }
    return chatMessages.map((message) => {
      const email = message.sender?.email;
      const participant = email ? chatParticipantsByEmail[email] : undefined;

      return {
        id: String(message.id),
        matterId: String(matter.id),
        senderId: String(message.senderId),
        senderEmail: email ?? undefined,
        senderName: participant?.name ?? email ?? undefined,
        senderAvatarUrl: participant?.avatarUrl,
        content: message.content,
        createdAt: new Date(message.createdAt).toISOString(),
      };
    });
  }, [matter, chatMessages, chatParticipantsByEmail]);

  const unreadCount = conversation?.unreadCount ?? 0;

  const downloadHistory = useCallback(async () => {
    if (!matter) {
      return;
    }

    try {
      const allMessages = await fetchAllMessages();
      if (!allMessages.length) {
        return;
      }

      const heading = getMatterDisplayHeading(matter);
      const participantLabel =
        chatOtherParty?.name ?? chatOtherParty?.email ?? 'unknown';
      const sorted = [...allMessages].sort(
        (messageA, messageB) =>
          new Date(messageA.createdAt).getTime() -
          new Date(messageB.createdAt).getTime(),
      );

      const lines = sorted.map((message) => {
        const timestamp = format(
          new Date(message.createdAt),
          'yyyy-MM-dd HH:mm',
        );
        const senderName = message.sender?.email ?? String(message.senderId);
        return `[${timestamp}] ${senderName}: ${message.content}`;
      });

      const header = `Chat History — ${heading}\nConversation with ${participantLabel}\nExported ${format(
        new Date(),
        'yyyy-MM-dd HH:mm',
      )}\n${'—'.repeat(40)}`;
      const content = [header, ...lines].join('\n');

      const safeParticipant = participantLabel
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-');
      const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `chat-history-${matter.id}-${safeParticipant}.txt`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: 'chat_history_export' },
        extra: { matterId: matter.id },
      });
    }
  }, [matter, chatOtherParty, fetchAllMessages]);

  const hasNoHiredExpert = Boolean(
    isAttorney && !expertsOnMatter.hasAnyHiredExpert,
  );
  const showActivityAndBilling = Boolean(
    (expertsOnMatter.hasAnyHiredExpert &&
      (isAttorney ||
        (currentUser?.role === 'Expert' &&
          expertsOnMatter.hiredExpertIds.includes(currentUser?.id ?? '')))) ||
      (isAttorney && pendingRequests.length > 0),
  );
  const hasAnyRetainerBalance = hasHoursBalance || hasFlatBalance;

  const isExpertOnMatter = Boolean(
    currentUser?.role === 'Expert' &&
      expertsOnMatter.hiredExpertIds.includes(currentUser?.id ?? ''),
  );

  const showExpertsTab = isAttorney;
  const hasAnyExperts =
    expertsOnMatter.hiredExperts.length > 0 ||
    expertsOnMatter.addedExperts.length > 0;
  const filteredHiredExperts = useMemo(() => {
    if (expertStatusFilter === ExpertStatus.All) {
      return expertsOnMatter.hiredExperts;
    }
    const statusMap: Partial<Record<ExpertStatus, ExpertRetainerRollupStatus>> =
      {
        [ExpertStatus.Pending]: ExpertRetainerRollupStatus.PendingApproval,
        [ExpertStatus.Active]: ExpertRetainerRollupStatus.Active,
      };
    const targetStatus = statusMap[expertStatusFilter];
    if (!targetStatus) {
      return [];
    }
    return expertsOnMatter.hiredExperts.filter((expert) => {
      const entry = expertsOnMatter.expertRetainerStatuses.find(
        (e) => e.expertId === expert.id,
      );
      return entry?.status === targetStatus;
    });
  }, [
    expertsOnMatter.hiredExperts,
    expertsOnMatter.expertRetainerStatuses,
    expertStatusFilter,
  ]);

  const status: MatterDetailStatus = (() => {
    if (!matterId) {
      return MatterDetailStatus.NotFound;
    }
    if (mattersQueryError && !matter) {
      return MatterDetailStatus.MattersError;
    }
    if (isMattersQueryLoading && !matter) {
      return MatterDetailStatus.MattersLoading;
    }
    if (!matter) {
      return MatterDetailStatus.NotFound;
    }
    if (!canAccessAsExpert) {
      return MatterDetailStatus.NoAccess;
    }
    if (expertMustCompleteStripe) {
      return MatterDetailStatus.RedirectStripe;
    }
    return MatterDetailStatus.Ready;
  })();

  const markRetainerActive = useCallback(
    async (retainerRequestId: string) => {
      if (!matterId) {
        return;
      }
      try {
        await markRetainerWorkActiveMutation(matterId, retainerRequestId);
        showToast('Retainer marked active.');
      } catch (error) {
        Sentry.captureException(error, {
          tags: { feature: 'mark_retainer_work_active' },
          extra: { matterId, retainerRequestId },
        });
        const message =
          error instanceof Error
            ? error.message
            : 'Something went wrong. Please try again.';
        showToast(message, 'error');
      }
    },
    [matterId, markRetainerWorkActiveMutation, showToast],
  );

  const markRetainerWorkComplete = useCallback(
    async (retainerRequestId: string) => {
      if (!matterId) {
        return;
      }
      try {
        await markRetainerWorkCompleteMutation(matterId, retainerRequestId);
        showToast('Work marked complete.');
      } catch (error) {
        Sentry.captureException(error, {
          tags: { feature: 'mark_retainer_work_complete' },
          extra: { matterId, retainerRequestId },
        });
        const message =
          error instanceof Error
            ? error.message
            : 'Something went wrong. Please try again.';
        showToast(message, 'error');
      }
    },
    [matterId, markRetainerWorkCompleteMutation, showToast],
  );

  return {
    matterContext: {
      status,
      matterId,
      matter: matter ?? null,
      mattersQueryError,
      currentUser: currentUser ?? null,
      navigate,
      isAttorney,
      isMatterClosed,
      canExpertRequestRetainer,
      isInvitedOnlyExpert,
      canAccessAsExpert,
      isExpertOnMatter,
      expertMustCompleteStripe,
      otherParty: otherParty ?? null,
      getExpertById,
      getUserById,
    },
    retainers: {
      expertsOnMatter,
      balance,
      activeRetainers,
      flatRateRetainers,
      pendingRequests,
      retainerExpertsForFilter,
      filteredPendingRequests,
      filteredFlatRateRetainers,
      filteredActiveRetainers,
      noRetainerBalance,
      hasHoursBalance,
      hasFlatBalance,
      hasAnyRetainerBalance,
      pendingApproveLabel,
      hasNoHiredExpert,
      showActivityAndBilling,
      historyRetainers,
      isLoadingRetainers,
      retainersError: retainersQueryError,
      retainerRequests: matterRetainersList,
      activatingRetainerId,
      completingRetainerId,
    },
    experts: {
      showExpertsTab,
      hasAnyExperts,
      filteredHiredExperts,
      showActivityAndBilling,
      hasNoHiredExpert,
    },
    modals: {
      editMatterModalOpen,
      setEditMatterModalOpen,
      matterToEdit,
      setMatterToEdit,
      rightDetailTab,
      setRightDetailTab,
      retainerStatusFilter,
      setRetainerStatusFilter,
      retainerExpertFilter,
      setRetainerExpertFilter,
      chatSidebarOpen,
      setChatSidebarOpen,
      chatExpertId,
      setChatExpertId,
      retainerModalOpen,
      setRetainerModalOpen,
      retainerModalExpertId,
      setRetainerModalExpertId,
      agreementRequestId,
      setAgreementRequestId,
      viewAgreementRequestId,
      setViewAgreementRequestId,
      viewOverviewRequestId,
      setViewOverviewRequestId,
      declineRequestId,
      setDeclineRequestId,
      declineMatterModalOpen,
      setDeclineMatterModalOpen,
      addExpertsModalOpen,
      setAddExpertsModalOpen,
      removeExpertModal,
      setRemoveExpertModal,
      closeRetainerModal,
      setCloseRetainerModal,
      nextDeadlineEdit,
      setNextDeadlineEdit,
      showDeadlinePicker,
      setShowDeadlinePicker,
      expertStatusFilter,
      setExpertStatusFilter,
    },
    chat: {
      chatOtherParty: chatOtherParty ?? null,
      chatMessagesForSidebar,
      isChatLoading,
      chatError: chatError ?? null,
      openChatWithExpert,
      closeChatSidebar,
      sendMessage,
      markConversationRead,
      unreadCount,
      downloadHistory,
    },
    actions: {
      getMatterById: (id: string) =>
        allMatters.find((matterItem) => matterItem.id === id) ?? undefined,
      getExpertById,
      closeMatter: async (matterIdToClose: string): Promise<void> => {
        await closeMatterMutation(matterIdToClose);
      },
      updateMatter: async (
        matterIdForUpdate: string,
        patch: Partial<UpdateMatterInput>,
      ): Promise<void> => {
        await updateMatterMutation(matterIdForUpdate, patch);
      },
      markRetainerActive,
      markRetainerWorkComplete,
    },
    store: {
      retainerRequests: matterRetainersList,
      experts: pickerExperts,
    },
  };
}
